import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { Box, Chip, ListItemButton, Stack, Typography } from "@mui/material";
import FileSearch from "components/fileStorage/FileSearch";
import {
  connectionLabel,
  useGetConnectionQuery,
} from "features/integrationsBeta/connections";
import type { ReadinessIssue } from "features/integrationsBeta/integrationsBeta";
import { worstSeverity } from "features/integrationsBeta/readinessUi";
import { useEffect, useState } from "react";
import ConnectionCard from "./ConnectionCard";

export interface ConnectionGroup {
  label: string;
  ids: number[];
}

function ConnItem({
  id,
  selected,
  search,
  issues,
  onSelect,
}: {
  id: number;
  selected: boolean;
  search: string;
  issues?: ReadinessIssue[];
  onSelect: (id: number) => void;
}) {
  const { data } = useGetConnectionQuery(id);
  const name = data ? connectionLabel(data) : `Connection ${id}`;
  const searchable = data ? `${name} ${data.provider_name}` : name;
  if (search && !searchable.toLowerCase().includes(search.toLowerCase()))
    return null;
  const severity = worstSeverity(issues);
  return (
    <ListItemButton
      selected={selected}
      onClick={() => onSelect(id)}
      sx={{ borderRadius: 1, gap: 1 }}
    >
      <Typography variant="body2" noWrap sx={{ flexGrow: 1, minWidth: 0 }}>
        {name}
      </Typography>
      {severity && (
        <WarningAmberIcon
          fontSize="small"
          color={severity === "error" ? "error" : "warning"}
        />
      )}
      {data?.type ? (
        <Chip label={data.type} size="small" variant="outlined" />
      ) : null}
    </ListItemButton>
  );
}

interface Props {
  groups: ConnectionGroup[];
  focus?: { id: number; nonce: number } | null;
  issuesByConnection?: Record<number, ReadinessIssue[]>;
}

/**
 * Master–detail Connections view: a narrow, searchable list grouped by the
 * pipeline stages that use each connection (plus the webhook Secret-Key
 * connection) on the left, and the selected connection's card
 * (config + credential slots) on the right.
 */
export default function ConnectionMasterDetail({
  groups,
  focus,
  issuesByConnection = {},
}: Props) {
  const [selected, setSelected] = useState<number | null>(null);
  const [search, setSearch] = useState("");

  const allIds = groups.flatMap((group) => group.ids);
  const allIdsKey = allIds.join(",");

  // allIdsKey stands in for allIds (a fresh array each render) as the dep.
  useEffect(() => {
    if (selected == null || !allIds.includes(selected)) {
      setSelected(allIds[0] ?? null);
    }
  }, [allIdsKey, selected]);

  // Jump requested from outside (readiness banner, step chip, webhook panel).
  useEffect(() => {
    if (focus && allIds.includes(focus.id)) {
      setSelected(focus.id);
    }
  }, [focus, allIdsKey]);

  if (!allIds.length) {
    return (
      <Typography variant="body2" color="text.secondary">
        No connections. Connections appear here once pipeline steps (or a
        webhook trigger) reference them.
      </Typography>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        gap: 2,
        flexDirection: { xs: "column", md: "row" },
      }}
    >
      <Box sx={{ width: { xs: "100%", md: 280 }, flexShrink: 0 }}>
        <Box sx={{ mb: 1 }}>
          <FileSearch
            onSearch={(t) => setSearch(t ?? "")}
            label="Search"
            size="small"
            fullWidth
          />
        </Box>
        <Stack
          spacing={1.5}
          sx={{ maxHeight: 460, overflowY: "auto", pr: 0.5 }}
        >
          {groups.map((group) => (
            <Box key={group.label}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ px: 1, fontWeight: 600 }}
              >
                {group.label} ({group.ids.length})
              </Typography>
              <Stack spacing={0.25} sx={{ mt: 0.5 }}>
                {group.ids.map((id) => (
                  <ConnItem
                    key={id}
                    id={id}
                    selected={selected === id}
                    search={search}
                    issues={issuesByConnection[id]}
                    onSelect={setSelected}
                  />
                ))}
              </Stack>
            </Box>
          ))}
        </Stack>
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        {selected != null ? (
          <ConnectionCard
            // Remount per connection so no component state (revealed
            // secrets, dialogs, drafts) can leak between connections.
            key={selected}
            connectionId={selected}
            issues={issuesByConnection[selected]}
          />
        ) : (
          <Typography variant="body2" color="text.secondary">
            Select a connection.
          </Typography>
        )}
      </Box>
    </Box>
  );
}
